/**
 * Carry-over (docs/mockups/install.html, Spec panel 7): the home-screen app on iOS, and
 * every in-app browser, starts with empty storage. Before the iPhone guide or a way-out
 * link opens we post a snapshot of this device's versioned local state for a one-time
 * code (`POST /v1/carry`, 30 minutes, 3 uses) and put `?carry=CODE` in the address and
 * the manifest link; the new app fetches it on its first launch and merges it, keeping
 * the same device id. No answers beyond the player's own saves, no personal data.
 *
 * `buildSnapshot` and `mergeSnapshot` are pure over a `KeyValueStore` (tested).
 */
import {
	addDays,
	countStatsRecords,
	type DateKey,
	isDateKey,
	isLevel,
	type Level,
	MAX_SAVED_STACKS,
	mergeStats,
	parseSavedStacks,
	restoreStats,
	serialiseSavedStacks,
	statsRecordCount,
} from '@arrow/game-engine';

import { parseSettings, SETTINGS_KEY } from '../app/settings.ts';
import { MAX_TAKEN, parseTaken, serializeTaken, TAKEN_KEY } from '../platform/challenges-taken.ts';
import type { KeyValueStore } from '../storage/kv.ts';
import { storageKeys } from '../storage/persistence.ts';
import { NESTS_KEY, parseNestMemory } from '../nests/store.ts';

/** The api refuses snapshots over 32 KB; we stay a little under, with the envelope. */
export const MAX_SNAPSHOT_BYTES = 30_000;
/** The query param (`/?carry=K7Q2`) and the code's shape. */
export const CARRY_PARAM = 'carry';
export const CARRY_CODE = /^[A-Za-z0-9]{4,8}$/;

export interface CarrySnapshot {
	v: 1;
	/** When it was taken (ISO). */
	at: string;
	/** `arrow:v1:*` key → its raw stored string. */
	keys: Record<string, string>;
}

const V = 'arrow:v1:';
const DEVICE_KEY = `${V}device`;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Everything worth carrying besides the day's saves (the device id goes beside the snapshot). */
const PROFILE_KEYS = [
	storageKeys.stats,
	`${V}first-seen`,
	`${V}last-level`,
	storageKeys.hintSeen,
	`${V}challenges:mine`,
	`${V}challenges:dismissed`,
	TAKEN_KEY,
	SETTINGS_KEY,
	storageKeys.stacks,
	// An internal device (James's, a tester's) stays one at the new address.
	`${V}internal`,
	// The nests this device is in (the gate and the Nest tab, nests/store.ts). Membership is
	// the device id's, so this is only written where the id comes along too.
	NESTS_KEY,
] as const;

/** Keys a snapshot may write on merge (anything else in it is ignored). */
const MERGEABLE = /^arrow:v1:(stats|first-seen|last-level|hint-seen|settings|stacks|internal|nests|challenges:mine|challenges:dismissed|challenges:taken|(game|flow|resume):daily:\d{4}-\d{2}-\d{2}:(easy|medium|hard))$/;

/** The challenge lists' cap (platform/challenges.ts). */
const MAX_CHALLENGES = 30;

const bytes = (s: string) => new TextEncoder().encode(s).length;

/**
 * The snapshot to carry: the stats (the streak), first-seen, prefs and settings, your own stacks on Play, the challenges this
 * device made and took, and today's and yesterday's daily saves (so a half-done puzzle comes
 * along). Kept under `maxBytes`: flows go first, then the day's saves, then the oldest
 * stats days (the streak is recent, so it survives).
 */
export function buildSnapshot(store: KeyValueStore, today: DateKey, now: Date, maxBytes = MAX_SNAPSHOT_BYTES): CarrySnapshot {
	const keys: Record<string, string> = {};
	for (const k of PROFILE_KEYS) {
		const v = store.get(k);
		if (v !== null) keys[k] = v;
	}
	const days = [today, addDays(today, -1)];
	const saves = store.keys().filter((k) => days.some((d) => k.startsWith(`${V}game:daily:${d}:`) || k.startsWith(`${V}resume:daily:${d}:`)));
	const flows = store.keys().filter((k) => days.some((d) => k.startsWith(`${V}flow:daily:${d}:`)));
	for (const k of [...saves, ...flows]) {
		const v = store.get(k);
		if (v !== null) keys[k] = v;
	}
	const snapshot: CarrySnapshot = { v: 1, at: now.toISOString(), keys };
	const size = () => bytes(JSON.stringify(snapshot));
	for (const k of flows) if (size() > maxBytes) delete keys[k];
	for (const k of saves) if (size() > maxBytes) delete keys[k];
	if (size() > maxBytes && keys[storageKeys.stats]) {
		// Keep the most recent days that fit.
		const stats = restoreStats(safeJson(keys[storageKeys.stats]!));
		const dates = Object.keys(stats.days).sort();
		while (dates.length > 0 && size() > maxBytes) {
			delete stats.days[dates.shift()!];
			keys[storageKeys.stats] = JSON.stringify(stats);
		}
	}
	for (const k of [`${V}challenges:dismissed`, TAKEN_KEY, `${V}challenges:mine`]) if (size() > maxBytes) delete keys[k];
	return snapshot;
}

function safeJson(raw: string): unknown {
	try {
		return JSON.parse(raw) as unknown;
	} catch {
		return undefined;
	}
}

/** Nothing played on this device yet: a carried snapshot may be merged in. */
export function isLocalStateEmpty(store: KeyValueStore): boolean {
	try {
		const raw = store.get(storageKeys.stats);
		if (raw !== null && statsRecordCount(restoreStats(safeJson(raw))) > 0) return false;
		return !store.keys().some((k) => k.startsWith(`${V}game:`));
	} catch {
		return false;
	}
}

/** Parses an untrusted snapshot (the api hands back whatever was posted). Null when it isn't one. */
export function parseSnapshot(raw: unknown): CarrySnapshot | null {
	if (typeof raw !== 'object' || raw === null) return null;
	const o = raw as Record<string, unknown>;
	if (o.v !== 1 || typeof o.keys !== 'object' || o.keys === null || Array.isArray(o.keys)) return null;
	const keys: Record<string, string> = {};
	for (const [k, v] of Object.entries(o.keys as Record<string, unknown>)) {
		if (MERGEABLE.test(k) && typeof v === 'string' && v.length <= 64_000) keys[k] = v;
	}
	return { v: 1, at: typeof o.at === 'string' ? o.at : '', keys };
}

/** Is this one stored value safe to write, the way the app's own parsers read it? */
function acceptable(key: string, value: string): string | null {
	if (key === storageKeys.stats) {
		const stats = restoreStats(safeJson(value));
		return statsRecordCount(stats) > 0 || stats.practice.completed > 0 ? JSON.stringify(stats) : null;
	}
	if (key === `${V}first-seen`) return isDateKey(value) ? value : null;
	if (key === `${V}last-level`) return isLevel(value) ? value : null;
	if (key === storageKeys.hintSeen || key === `${V}internal`) return value === '1' ? value : null;
	if (key === SETTINGS_KEY) return JSON.stringify(parseSettings(value));
	if (key === storageKeys.stacks) {
		const stacks = parseSavedStacks(safeJson(value));
		return stacks.length > 0 ? JSON.stringify(serialiseSavedStacks(stacks)) : null;
	}
	if (key === NESTS_KEY) {
		const memory = parseNestMemory(value);
		return memory.nests.length > 0 ? JSON.stringify(memory) : null;
	}
	if (key === TAKEN_KEY) {
		const taken = parseTaken(value);
		return taken.length > 0 ? serializeTaken(taken) : null;
	}
	// Saves, flows, resume summaries and the challenge lists: well-formed JSON objects or
	// arrays. Their own readers (restoreGame, restoreFlow, …) still validate on every load.
	const parsed = safeJson(value);
	return typeof parsed === 'object' && parsed !== null ? value : null;
}

export type MergeResult = { merged: false; reason: 'not-empty' | 'invalid' } | { merged: true; keys: number; deviceId: string | null };

/**
 * Merges a carried snapshot into this device's storage, but only when nothing has been
 * played here yet (the home-screen app's first launch, which may be re-opened with the
 * same code). Each value is checked the way the app's defensive parsers read it; anything
 * else is skipped. The carried device id replaces this one, so analytics sees one player.
 * Never throws. A device that has played gets {@link combineSnapshot} instead.
 */
export function mergeSnapshot(store: KeyValueStore, raw: { deviceId?: unknown; snapshot?: unknown }): MergeResult {
	try {
		if (!isLocalStateEmpty(store)) return { merged: false, reason: 'not-empty' };
		return combineSnapshot(store, raw);
	} catch {
		return { merged: false, reason: 'invalid' };
	}
}

const DAILY_SLOT = /^arrow:v1:(?:game|flow|resume):(daily:(\d{4}-\d{2}-\d{2}):(easy|medium|hard))$/;
const slotKeys = (slot: string) => [`${V}game:${slot}`, `${V}flow:${slot}`, `${V}resume:${slot}`];

function parseList<T>(raw: string | null, read: (parsed: unknown) => T[]): T[] {
	return raw === null ? [] : read(safeJson(raw));
}
const itemsOf = (parsed: unknown): unknown[] => {
	const o = parsed as { v?: unknown; items?: unknown } | null;
	return o?.v === 1 && Array.isArray(o.items) ? o.items : [];
};
const codeOf = (item: unknown): string | null => {
	const code = (item as { code?: unknown } | null)?.code;
	return typeof code === 'string' ? code : null;
};
/** `ours` then whatever of `theirs` has a key not seen yet, the last `max` kept. */
function union<T>(ours: readonly T[], theirs: readonly T[], key: (t: T) => string | null, max: number): T[] {
	const seen = new Set(ours.map(key));
	return [...ours, ...theirs.filter((t) => !seen.has(key(t)))].slice(-max);
}

/**
 * The value to store for `key` once a carried (already accepted) value meets the one
 * stored here (`ours`, possibly null): never a loss on either side. Null: leave it be.
 */
function combineValue(key: string, ours: string | null, theirs: string): string | null {
	if (ours === null) return theirs;
	if (key === storageKeys.stats) return JSON.stringify(mergeStats(restoreStats(safeJson(ours)), restoreStats(safeJson(theirs))));
	if (key === `${V}first-seen`) return isDateKey(ours) && ours <= theirs ? null : theirs;
	if (key === storageKeys.stacks) {
		const mine = parseSavedStacks(safeJson(ours));
		const names = new Set(mine.map((s) => s.name.toLowerCase()));
		const extra = parseSavedStacks(safeJson(theirs)).filter((s) => !names.has(s.name.toLowerCase()));
		return JSON.stringify(serialiseSavedStacks(union(mine, extra, (s) => s.id, MAX_SAVED_STACKS).slice(0, MAX_SAVED_STACKS)));
	}
	if (key === TAKEN_KEY) return serializeTaken(union(parseTaken(ours), parseTaken(theirs), (t) => t.code, MAX_TAKEN));
	if (key === `${V}challenges:mine`) {
		return JSON.stringify({ v: 1, items: union(parseList(ours, itemsOf), parseList(theirs, itemsOf), codeOf, MAX_CHALLENGES) });
	}
	if (key === `${V}challenges:dismissed`) {
		const list = (raw: string) => {
			const parsed = safeJson(raw);
			return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
		};
		return JSON.stringify(union(list(ours), list(theirs), (c) => c, MAX_CHALLENGES));
	}
	// The rest (settings, last level, the hint, internal) are choices made here: ours stand.
	return null;
}

/** Whether `stats` has (date, level) finished. */
function finished(stats: ReturnType<typeof restoreStats>, date: DateKey, level: Level): boolean {
	return stats.days[date]?.[level]?.completedAt != null;
}

/**
 * Merges a carried snapshot into this device's storage, whatever is already here (the
 * move to a new address, where the player may have played already). Nothing is lost:
 * the stats are unioned (`mergeStats`: the longer streak survives), first-seen is the
 * earlier, stacks and challenge lists are unioned; settings and other choices made here
 * stand. A day's save (game, Flow, resume summary) moves as one: the carried one is taken
 * when there's none here, or when only the carried side finished that puzzle. The carried
 * device id is adopted only when nothing was played here (so analytics sees one player).
 * Each value is checked the way the app's own parsers read it. Never throws.
 */
export function combineSnapshot(store: KeyValueStore, raw: { deviceId?: unknown; snapshot?: unknown }): MergeResult {
	try {
		const snapshot = parseSnapshot(raw.snapshot);
		if (!snapshot) return { merged: false, reason: 'invalid' };
		const wasEmpty = isLocalStateEmpty(store);
		const ourStats = restoreStats(safeJson(store.get(storageKeys.stats) ?? ''));
		const theirStats = restoreStats(safeJson(snapshot.keys[storageKeys.stats] ?? ''));
		let n = 0;
		const write = (k: string, v: string) => {
			if (store.set(k, v)) n++;
		};
		const slots = new Map<string, { date: DateKey; level: Level }>();
		for (const [k, v] of Object.entries(snapshot.keys)) {
			const ok = acceptable(k, v);
			if (ok === null) continue;
			// Nests belong to the carried device id, which only replaces this one on an empty device.
			if (k === NESTS_KEY && !wasEmpty) continue;
			const slot = DAILY_SLOT.exec(k);
			if (slot) {
				slots.set(slot[1]!, { date: slot[2]!, level: slot[3] as Level });
				continue;
			}
			const ours = store.get(k);
			if (k === storageKeys.stats && ours !== null) {
				// A blob we can't fully read is kept aside, as persistence.ts does, never silently lost.
				if (countStatsRecords(safeJson(ours)) > statsRecordCount(restoreStats(safeJson(ours)))) store.set(storageKeys.statsBackup, ours);
			}
			const next = combineValue(k, ours, ok);
			if (next !== null && next !== ours) write(k, next);
		}
		for (const [slot, { date, level }] of slots) {
			const keys = slotKeys(slot);
			const ours = keys.some((k) => store.get(k) !== null);
			const take = !ours || (finished(theirStats, date, level) && !finished(ourStats, date, level));
			if (!take) continue;
			for (const k of keys) {
				const v = snapshot.keys[k];
				const ok = v === undefined ? null : acceptable(k, v);
				if (ok !== null) write(k, ok);
				else store.remove(k);
			}
		}
		const deviceId = wasEmpty && typeof raw.deviceId === 'string' && UUID.test(raw.deviceId) ? raw.deviceId : null;
		if (deviceId) store.set(DEVICE_KEY, deviceId);
		return { merged: true, keys: n, deviceId };
	} catch {
		return { merged: false, reason: 'invalid' };
	}
}

/** The `carry` code in a url, if it has a well-formed one. */
export function carryCodeIn(url: string): string | null {
	try {
		const code = new URL(url).searchParams.get(CARRY_PARAM);
		return code && CARRY_CODE.test(code) ? code : null;
	} catch {
		return null;
	}
}

/** `url` with `carry` set to `code` (or removed, with null). Path, other params and hash are kept. */
export function withCarry(url: string, code: string | null): string {
	const u = new URL(url);
	if (code) u.searchParams.set(CARRY_PARAM, code);
	else u.searchParams.delete(CARRY_PARAM);
	const q = u.searchParams.toString();
	return `${u.pathname}${q ? `?${q}` : ''}${u.hash}`;
}

/**
 * After the api has folded this device into the carried one (`POST /v1/carry/:code/claim`):
 * merge the snapshot as {@link combineSnapshot} does (nothing lost on either side), then
 * take on the carried device id and join the two nest lists, since the memberships now all
 * belong to that id. Only call it once the claim succeeded. Never throws.
 */
export function adoptSnapshot(store: KeyValueStore, raw: { deviceId?: unknown; snapshot?: unknown }): MergeResult {
	try {
		if (typeof raw.deviceId !== 'string' || !UUID.test(raw.deviceId)) return { merged: false, reason: 'invalid' };
		const res = combineSnapshot(store, raw);
		if (!res.merged) return res;
		const theirs = parseSnapshot(raw.snapshot)?.keys[NESTS_KEY];
		const carried = parseNestMemory(theirs ?? null);
		const ours = parseNestMemory(store.get(NESTS_KEY));
		const nests = [...carried.nests, ...ours.nests.filter((n) => !carried.nests.some((c) => c.id === n.id))].slice(0, 10);
		if (nests.length > 0) {
			const current = ours.current ?? carried.current;
			store.set(NESTS_KEY, JSON.stringify({ v: 1, nests, current: nests.some((n) => n.id === current) ? current : null, name: ours.name ?? carried.name }));
		}
		store.set(DEVICE_KEY, raw.deviceId);
		return { merged: true, keys: res.keys, deviceId: raw.deviceId };
	} catch {
		return { merged: false, reason: 'invalid' };
	}
}
